import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from './supabase';
import { useCartStore } from '../hooks/useCart';
import { generateDeviceFingerprint, getDeviceInfo } from '@lancar-pos/shared';
import type { Session } from '@supabase/supabase-js';

export interface User {
  id: string;
  email: string;
  full_name?: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isOffline: boolean;
  initialized: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (fullName: string) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  checkLicense: () => Promise<{ valid: boolean; tier: string | null; error: Error | null }>;
}

let authSubscription: { unsubscribe: () => void } | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isLoading: true,
      isAuthenticated: false,
      isOffline: false,
      initialized: false,

      initialize: async () => {
        const { initialized } = get();
        if (initialized) return;

        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            let profile = null;
            try {
              const { data } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', session.user.id)
                .maybeSingle();
              profile = data;
            } catch (profileError) {
              console.warn('Could not fetch profile, using cached data:', profileError);
            }

            set({
              user: {
                id: session.user.id,
                email: session.user.email || '',
                full_name: profile?.full_name || undefined,
              },
              session,
              isAuthenticated: true,
              isLoading: false,
              isOffline: false,
              initialized: true,
            });
          } else {
            set({ isLoading: false, isOffline: false, initialized: true });
          }

          if (authSubscription) {
            authSubscription.unsubscribe();
          }

          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
              if (event === 'SIGNED_IN' && session?.user) {
                let profile = null;
                try {
                  const { data } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', session.user.id)
                    .maybeSingle();
                  profile = data;
                } catch (profileError) {
                  console.warn('Could not fetch profile on auth change:', profileError);
                }

                set({
                  user: {
                    id: session.user.id,
                    email: session.user.email || '',
                    full_name: profile?.full_name || undefined,
                  },
                  session,
                  isAuthenticated: true,
                  isLoading: false,
                });
              } else if (event === 'SIGNED_OUT') {
                set({
                  user: null,
                  session: null,
                  isAuthenticated: false,
                  isLoading: false,
                });
              }
            }
          );

          authSubscription = subscription;
        } catch (error) {
          console.error('Auth initialization error:', error);
          const cachedAuth = get();
          if (cachedAuth.isAuthenticated && cachedAuth.user) {
            set({ isLoading: false, isOffline: true, initialized: true });
          } else {
            set({ isLoading: false, isOffline: false, initialized: true });
          }
        }
      },

      signIn: async (email: string, password: string) => {
        try {
          set({ isLoading: true });
          
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) throw error;

          if (data.user) {
            let profile = null;
            try {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', data.user.id)
                .maybeSingle();
              profile = profileData;
            } catch (profileError) {
              console.warn('Could not fetch profile after sign in:', profileError);
            }

            set({
              user: {
                id: data.user.id,
                email: data.user.email || '',
                full_name: profile?.full_name || undefined,
              },
              session: data.session,
              isAuthenticated: true,
              isLoading: false,
              isOffline: false,
            });
          }

          return { error: null };
        } catch (error) {
          set({ isLoading: false });
          return { error: error as Error };
        }
      },

      signUp: async (email: string, password: string, fullName?: string) => {
        try {
          set({ isLoading: true });
          
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: fullName,
              },
            },
          });

          if (error) throw error;

          // Profile is auto-created by on_auth_user_created trigger
          // Just wait a moment for trigger to complete, then fetch profile
          if (data.user) {
            if (data.session) {
              // User is auto-confirmed, fetch profile
              let profile = null;
              try {
                // Small delay to allow trigger to complete
                await new Promise(resolve => setTimeout(resolve, 500));
                const { data: profileData } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', data.user.id)
                  .maybeSingle();
                profile = profileData;
              } catch (profileError) {
                console.warn('Could not fetch profile after signup:', profileError);
              }

              set({
                user: {
                  id: data.user.id,
                  email: data.user.email || '',
                  full_name: profile?.full_name || fullName,
                },
                session: data.session,
                isAuthenticated: true,
                isLoading: false,
              });
            } else {
              // Email confirmation required
              set({ isLoading: false });
            }
          }

          return { error: null };
        } catch (error) {
          set({ isLoading: false });
          return { error: error as Error };
        }
      },

      signOut: async () => {
        try {
          await supabase.auth.signOut();
          useCartStore.getState().clearCart();
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isOffline: false,
          });
        } catch (error) {
          console.error('Sign out error:', error);
          useCartStore.getState().clearCart();
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isOffline: false,
          });
        }
      },

      updateProfile: async (fullName: string) => {
        const { user } = get();
        if (!user) return { error: new Error('Not authenticated') };

        try {
          const { error } = await supabase
            .from('profiles')
            .upsert(
              {
                id: user.id,
                email: user.email,
                full_name: fullName,
                updated_at: new Date().toISOString(),
              },
              { onConflict: 'id' }
            );

          if (error) throw error;

          set({
            user: {
              ...user,
              full_name: fullName,
            },
          });

          return { error: null };
        } catch (error) {
          return { error: error as Error };
        }
      },

      resetPassword: async (email: string) => {
        try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/app/reset-password`,
          });

          if (error) throw error;

          return { error: null };
        } catch (error) {
          return { error: error as Error };
        }
      },

      updatePassword: async (newPassword: string) => {
        try {
          const { error } = await supabase.auth.updateUser({
            password: newPassword,
          });

          if (error) throw error;

          return { error: null };
        } catch (error) {
          return { error: error as Error };
        }
      },

      checkLicense: async () => {
        const { user } = get();
        if (!user) {
          return { valid: false, tier: null, error: new Error('Not authenticated') };
        }

        try {
          const fingerprint = await generateDeviceFingerprint();
          const deviceInfo = getDeviceInfo();

          const { data, error } = await supabase
            .from('licenses')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .maybeSingle();

          if (error) throw error;

          if (!data) {
            return { valid: false, tier: null, error: new Error('No active license found') };
          }

          if (data.device_fingerprint && data.device_fingerprint !== fingerprint) {
            return {
              valid: false,
              tier: null,
              error: new Error('Lisensi sudah digunakan di perangkat lain'),
            };
          }

          if (!data.device_fingerprint) {
            await supabase
              .from('licenses')
              .update({
                device_fingerprint: fingerprint,
                device_info: deviceInfo,
                activated_at: data.activated_at || new Date().toISOString(),
              })
              .eq('id', data.id);
          }

          return { valid: true, tier: data.tier, error: null };
        } catch (error) {
          return { valid: false, tier: null, error: error as Error };
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
